import React, {FC, useEffect} from "react";
import styled from "styled-components";
import {useLocalStorage} from "../hooks/useLocalStorage";
import {useStorageData} from "../hooks/useStorageData";
import {getAttributeValueFromCurrentURL} from "../utils/url";
import {useTranslation} from "react-i18next";

const Wrapper = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.2);
`

const Banner = styled.div`
  font-family: "Roboto", "Manrope", sans-serif !important;
  z-index: 9999999;
  background: #ffffff;
  border: 1px solid #E3E5E5;
  margin: 5px;
  color: black;
  box-shadow: 0 2px 5px 0 #000000a8;
  bottom: 8px;
  left: 8px;
  width: calc(100% - 30px);
  border-radius: 5px;
  align-items: center;
  display: flex;
  position: fixed;
  flex-wrap: wrap;
  justify-content: space-between;
`

const Content = styled.div`
  flex: 1 0 300px;
  margin: 15px;
`

const ButtonWrapper = styled.div`
  display: flex;
  margin: 15px;
  @media (max-width: 544px) {
   width: 100%;
  }
  
  .button {
    cursor: pointer;
    border-radius: 5px;
    font-size: 15px;
    padding: 10px;
  }
  
  .accept-button {
    color: #ffffff;
    background: #1976d2;
    border: 1px solid #1976d2;
    @media (max-width: 544px) {
     width: 100%;
    }
  }
  .decline-button {
    background: #ffffff;
    color: rgb(0 0 0 / 84%);
    border: 1px solid rgb(0 0 0 / 50%);
    margin-left: 15px;
  }
`

type PropsType = {
  display: boolean;
  debug?: boolean;
  expires?: number;
}

const DAY = 1000 * 60 * 60 * 24

// no answer, or one older than `expires` days
export const isCookieAnswerExpired = (answerDate: string | null, expires: number, now = new Date()): boolean => {
  if (!answerDate) {
    return true
  }
  const days = Math.floor((now.getTime() - new Date(answerDate).getTime()) / DAY)
  return days > expires
}

/**
 * Asks for the cookie consent where the widget has to: full screen on its own page (`display`).
 * Embedded in a client site or in an iframe the site asks for it, see analytics/Mixpanel.
 * A page opened with ?cookies-banner=false accepts the cookies without asking.
 */
const CookieBanner: FC<PropsType> = ({
  debug = false,
  display,
  expires = 30, // days
}) => {
  const { t } = useTranslation()
  const [, setCookieConsent] = useLocalStorage<boolean>('cookie-consent', false);
  const [, setAnswerDate] = useLocalStorage<string | null>('cookie-answer-date', null);
  const acceptedByUrl = getAttributeValueFromCurrentURL('cookies-banner') === 'false';
  const cookieAnswerDate = useStorageData('cookie-answer-date')

  const answer = (consent: boolean): void => {
    setCookieConsent(consent)
    setAnswerDate(new Date())
  };

  useEffect(() => {
    if (acceptedByUrl) {
      answer(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedByUrl])

  const showBanner = debug || (display && !acceptedByUrl && isCookieAnswerExpired(cookieAnswerDate, expires))

  return (
    <Wrapper style={{display: showBanner ? 'block' : 'none'}}>
      <Banner>
        <Content>
          {t('cookieBanner_text')}{' '}
          <a href="https://www.haccess.io/privacy-policy" target='_blank' rel="noopener noreferrer">{t('cookieBanner_readMore')}</a>
        </Content>
        <ButtonWrapper>
          <button className={'button accept-button'} onClick={() => answer(true)}>
            {t('cookieBanner_accept')}
          </button>
          <button className={'button decline-button'} onClick={() => answer(false)}>
            {t('cookieBanner_decline')}
          </button>
        </ButtonWrapper>
      </Banner>
    </Wrapper>
  )
}

export default CookieBanner
